import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { CreateSpeakingSessionFormValues, schema } from './validation';
import { speakingSessionStore } from 'store';
import { Timestamp } from '@britishcouncil/common-core';
import { useCallback, useEffect, useMemo } from 'react';
import { useNavigationContext, useServerErrors } from 'hooks';
import { formatApiDate, parseServerErrors } from 'utils';
import { useNavigate, generatePath } from 'react-router';
import { ROUTING_PATHS } from 'const';
import { toast } from '@britishcouncil/react-admin';

export const useCreateSpeakingSession = () => {
  const { locationId } = useNavigationContext();
  const navigate = useNavigate();
  const { speakingSessionDetails } = ROUTING_PATHS;

  const form = useForm<CreateSpeakingSessionFormValues>({
    resolver: yupResolver(schema),
    mode: 'all',
    defaultValues: {},
  });

  const [speakingSessionDate] = form.watch(['sessionDate']);

  const sessionDate = useMemo(() => {
    return speakingSessionDate ? Timestamp.toISODate(speakingSessionDate) : undefined;
  }, [speakingSessionDate]);

  const { data: availableVenues, isFetching: isFetchingVenues } =
    speakingSessionStore.service.useGetAvailableVenuesForSpeakingSessionQuery(
      {
        sessionDate: sessionDate!,
        locationId: locationId!,
      },
      { skip: !sessionDate || !locationId }
    );

  const { data: availableDates, isFetching: isFetchingDates } =
    speakingSessionStore.service.useGetAvailableDatesForSpeakingSessionQuery(
      {
        locationId: locationId!,
      },
      { skip: !locationId, refetchOnMountOrArgChange: true }
    );

  const disabledDates = useMemo(() => {
    const enabledDates = new Set((availableDates ?? []).map((x) => x.date.split('T')[0]));
    return (date: Date) => !enabledDates.has(formatApiDate(date));
  }, [availableDates]);

  const [
    createSpeakingSession,
    {
      isLoading: isCreating,
      isSuccess: created,
      error: createError,
      data: speakingSessionId,
      reset,
    },
  ] = speakingSessionStore.service.useCreateSpeakingSessionMutation();

  const goToSpeakingSessionDetails = useCallback(
    (sessionId: number) =>
      navigate(generatePath(speakingSessionDetails.absolutePath, { sessionId })),
    [navigate, speakingSessionDetails.absolutePath]
  );

  useEffect(() => {
    if (created) {
      toast({
        type: 'success',
        title: 'Success',
        message: 'Changes saved successfully',
      });
      goToSpeakingSessionDetails(speakingSessionId);
      reset();
    }
  }, [goToSpeakingSessionDetails, created, speakingSessionId, reset]);

  const serverErrors = useMemo(() => parseServerErrors(createError), [createError]);

  const { generalErrors, clearServerErrors } = useServerErrors(
    form.getValues(),
    serverErrors,
    form.setError
  );

  const handleSubmit = (data: CreateSpeakingSessionFormValues) => {
    clearServerErrors();

    createSpeakingSession({
      sessionDate: Timestamp.toISODate(data.sessionDate),
      testTakerAdmissionTimeMinutes: data.testTakerAdmissionTimeMinutes ?? 0,
      venueId: data.venueId,
    });
  };

  return {
    form,
    handleSubmit,
    generalErrors,
    venueOptions: availableVenues?.map((x) => ({ text: x.name, value: x.id })) ?? [],
    isFetchingVenues,
    isFetchingDates,
    areVenueOptionsReady: !!availableVenues && !isFetchingVenues,
    areDateOptionsReady: !!availableDates && !isFetchingDates,
    isCreating,
    created,
    disabledDates,
  };
};
